/**
 * 批量并发队列
 * - 多个 key 合并成一个批次执行
 * - 批次级别并发控制
 * - 支持延迟合并（debounce）
 * - 相同 key 请求合并
 * - 支持取消
 */

type Callback<V> = {
  resolve: (value: V) => void;
  reject: (error: Error) => void;
};

export interface BatchConcurrentQueueOptions<K, V> {
  /** 批次最大并发数 */
  concurrency: number;
  /** 单批次最大 key 数量 */
  batchSize: number;
  /** 批量执行任务的函数，返回 key-value 映射 */
  executor: (keys: K[], signal: AbortSignal) => Promise<Map<K, V>>;
  /** 延迟执行时间（ms），用于合并短时间内的请求 */
  debounceMs?: number;
  /** 可选：成功回调（批次级别） */
  onBatchSuccess?: (results: Map<K, V>) => void;
  /** 可选：失败回调（批次级别） */
  onBatchError?: (keys: K[], error: Error) => void;
  /** 可选：单个 key 成功回调 */
  onSuccess?: (key: K, value: V) => void;
  /** 可选：单个 key 失败回调（未找到结果时） */
  onError?: (key: K, error: Error) => void;
}

export class BatchConcurrentQueue<K, V> {
  private options: BatchConcurrentQueueOptions<K, V>;
  private waitingQueue: K[] = [];
  private waitingCallbackMap = new Map<K, Callback<V>[]>();
  private pendingCallbackMap = new Map<K, Callback<V>[]>();
  private pendingAbortMap = new Map<number, AbortController>();
  private currentConcurrent = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private batchIdCounter = 0;

  constructor(options: BatchConcurrentQueueOptions<K, V>) {
    this.options = {
      debounceMs: 0,
      ...options,
    };
  }

  /** 加入队列执行 */
  enqueue(key: K, priority: 'high' | 'normal' = 'normal'): Promise<V> {
    return new Promise((resolve, reject) => {
      // 已在 pending 中，复用
      if (this.pendingCallbackMap.has(key)) {
        this.pendingCallbackMap.get(key)?.push({ resolve, reject });
        return;
      }

      // 加入等待队列
      if (!this.waitingCallbackMap.has(key)) {
        this.waitingCallbackMap.set(key, []);
        if (priority === 'high') {
          this.waitingQueue.unshift(key);
        } else {
          this.waitingQueue.push(key);
        }
      }
      this.waitingCallbackMap.get(key)?.push({ resolve, reject });

      this.scheduleExecution();
    });
  }

  /** 取消指定 key 的任务（仅限等待队列中的） */
  cancel(key: K): void {
    const idx = this.waitingQueue.indexOf(key);
    if (idx !== -1) {
      this.waitingQueue.splice(idx, 1);
      this.waitingCallbackMap.delete(key);
    }
  }

  /** 是否正在处理中 */
  isPending(key: K): boolean {
    return this.pendingCallbackMap.has(key);
  }

  /** 是否在等待队列中 */
  isWaiting(key: K): boolean {
    return this.waitingCallbackMap.has(key);
  }

  /** 获取当前等待队列大小 */
  getWaitingSize(): number {
    return this.waitingQueue.length;
  }

  /** 获取当前并发数 */
  getCurrentConcurrent(): number {
    return this.currentConcurrent;
  }

  /** 调度执行 */
  private scheduleExecution(): void {
    const { batchSize, debounceMs } = this.options;

    // 达到批次大小阈值，立即尝试执行
    if (this.waitingQueue.length >= batchSize) {
      this.tryExecuteBatch();
      return;
    }

    // 使用 debounce 延迟执行
    if (debounceMs && debounceMs > 0) {
      if (!this.debounceTimer) {
        this.debounceTimer = setTimeout(() => {
          this.debounceTimer = null;
          this.tryExecuteBatch();
        }, debounceMs);
      }
    } else {
      // 无延迟，立即尝试执行
      this.tryExecuteBatch();
    }
  }

  /** 尝试执行批量请求 */
  private tryExecuteBatch(): void {
    const { concurrency, batchSize } = this.options;

    // 检查并发数限制
    if (this.currentConcurrent >= concurrency) {
      return;
    }

    // 检查是否有待处理的请求
    if (this.waitingQueue.length === 0) {
      return;
    }

    // 清除 debounce 定时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // 取出本批次要请求的 keys（最多 batchSize 个）
    const batchKeys: K[] = [];
    for (const key of this.waitingQueue) {
      batchKeys.push(key);
      if (batchKeys.length >= batchSize) {
        break;
      }
    }

    // 将回调从 waiting 转移到 pending
    batchKeys.forEach((key) => {
      const idx = this.waitingQueue.indexOf(key);
      if (idx !== -1) {
        this.waitingQueue.splice(idx, 1);
      }
      const callbacks = this.waitingCallbackMap.get(key) || [];
      this.waitingCallbackMap.delete(key);

      // 合并到 pending（可能已有之前的回调）
      const existingPending = this.pendingCallbackMap.get(key) || [];
      this.pendingCallbackMap.set(key, [...existingPending, ...callbacks]);
    });

    // 执行批量请求
    this.executeBatch(batchKeys);

    // 如果 waitingQueue 还有剩余，继续调度
    if (this.waitingQueue.length > 0) {
      this.scheduleExecution();
    }
  }

  /** 执行批量请求 */
  private async executeBatch(batchKeys: K[]): Promise<void> {
    const { executor, onBatchSuccess, onBatchError, onSuccess, onError } = this.options;

    this.currentConcurrent++;
    const batchId = ++this.batchIdCounter;
    const controller = new AbortController();
    this.pendingAbortMap.set(batchId, controller);

    try {
      const results = await executor(batchKeys, controller.signal);

      // 批次成功回调
      onBatchSuccess?.(results);

      // 处理成功返回的结果
      results.forEach((value, key) => {
        onSuccess?.(key, value);

        // 通知所有 pending 回调
        const callbacks = this.pendingCallbackMap.get(key);
        if (callbacks) {
          callbacks.forEach((cb) => cb.resolve(value));
          this.pendingCallbackMap.delete(key);
        }

        // 同时检查 waiting 中是否有新加入的相同 key（复用结果）
        const waitingCallbacks = this.waitingCallbackMap.get(key);
        if (waitingCallbacks) {
          waitingCallbacks.forEach((cb) => cb.resolve(value));
          this.waitingCallbackMap.delete(key);
          const idx = this.waitingQueue.indexOf(key);
          if (idx !== -1) {
            this.waitingQueue.splice(idx, 1);
          }
        }
      });

      // 处理未找到的 key
      const notFoundError = new Error('Not found in batch result');
      batchKeys.forEach((key) => {
        const callbacks = this.pendingCallbackMap.get(key);
        if (callbacks) {
          onError?.(key, notFoundError);
          callbacks.forEach((cb) => cb.reject(notFoundError));
          this.pendingCallbackMap.delete(key);
        }
      });
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return;
      }

      onBatchError?.(batchKeys, err);

      // 通知所有相关回调
      batchKeys.forEach((key) => {
        const callbacks = this.pendingCallbackMap.get(key);
        if (callbacks) {
          callbacks.forEach((cb) => cb.reject(err));
          this.pendingCallbackMap.delete(key);
        }
      });
    } finally {
      this.pendingAbortMap.delete(batchId);
      this.currentConcurrent--;

      // 请求完成后，尝试处理等待队列中的下一批
      if (this.waitingQueue.length > 0) {
        this.tryExecuteBatch();
      }
    }
  }
}
